import {
  DevHubMenuItem,
  DevHubTextField,
  DevHubBox,
} from '@devxdiscover/devhub-ui';

import {
  ChangeEvent,
  SetStateAction,
  Dispatch,
  useState,
  useEffect,
} from 'react';
import { Progress } from '@backstage/core-components';
import { gql, useQuery } from '@apollo/client';
import { Application, OptionItem } from './types/InsightsTypes';

export interface Props {
  app: Application | undefined;
  options: OptionItem[];
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  setComponentHasError: Dispatch<SetStateAction<boolean>>;
}

const AppGroupQuery = gql`
  query AppGroupForServiceLevelTier($filter: ResourceCatalogApplicationFilter) {
    resources {
      applicationGroups(filter: $filter) {
        name
        serviceLevel
      }
    }
  }
`;

/**
 * ServiceTier component to select application tier from App360 page
 * @param props props of the component
 * @param props.appGroup application group name
 * @param props.options the list of service level tiers to select from
 * @param props.onChange function to handle change event
 * @param props.setComponentHasError function to set error state
 */
export function ServiceTier(props: Props) {
  const [tier, setTier] = useState<number | undefined>(undefined);
  const [appGroupTier, setAppGroupTier] = useState<number | undefined>(
    undefined,
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTier(Number(e?.target?.value));
    props?.onChange(e);
  };

  useEffect(() => {
    if (props?.app?.serviceLevel) {
      setTier(props?.app?.serviceLevel);
    }
  }, [props?.app?.serviceLevel]);

  const validateTier = (): boolean => {
    let isInvalid = true;
    if (tier && appGroupTier) {
      isInvalid = tier < appGroupTier;
    }
    props?.setComponentHasError(isInvalid);
    return isInvalid;
  };

  const { loading, data } = useQuery(AppGroupQuery, {
    errorPolicy: 'all',
    variables: {
      filter: {
        applicationName: props?.app?.appGroup,
      },
    },
  });

  useEffect(() => {
    if (!data) return;
    if (data?.resources?.applicationGroups.length === 0) return;
    setAppGroupTier(data?.resources?.applicationGroups[0].serviceLevel);
  }, [data, props?.app?.appGroup]);

  return (
    <>
      {loading ? (
        <DevHubBox width="100%">
          <Progress />
        </DevHubBox>
      ) : (
        <DevHubTextField
          name="serviceLevelTier"
          size="small"
          select
          error={validateTier()}
          helperText={
            validateTier()
              ? 'Service Tier of an Application is higher than the Application Group Tier'
              : ''
          }
          variant="outlined"
          label="Select"
          onChange={handleChange}
          value={tier}
        >
          {props?.options?.map((option: OptionItem) => {
            return (
              <DevHubMenuItem key={option.value} value={option.value}>
                {option.label}
              </DevHubMenuItem>
            );
          })}
        </DevHubTextField>
      )}
    </>
  );
}
